import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { MediaCreatorApi, VoiceSummary } from './MediaCreatorApi';

export class MediaCreatorClient implements MediaCreatorApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async generateSpeech(
    text: string,
    id: string,
    format?: string,
    stability?: number,
    languageCode?: string,
  ): Promise<Blob> {
    const baseUrl = await this.discoveryApi.getBaseUrl('media-creator');
    const params = new URLSearchParams({ text, voiceId: id });
    if (format) params.set('format', format);
    if (typeof stability === 'number')
      params.set('stability', String(stability));
    if (languageCode) params.set('languageCode', languageCode);
    const response = await this.fetchApi.fetch(
      `${baseUrl}/generatespeech?${params.toString()}`,
    );
    if (!response.ok) {
      let errorBody: any = undefined;
      try {
        errorBody = await response.json();
      } catch (e) {
        // fallback if not JSON
        errorBody = await response.text();
      }
      const error = new Error(
        `Failed to generate speech: ${response.statusText}`,
      );
      (error as any).body = errorBody;
      throw error;
    }
    return await response.blob();
  }

  async listVoices(): Promise<VoiceSummary[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('media-creator');
    const response = await this.fetchApi.fetch(`${baseUrl}/listvoices`);
    if (!response.ok) {
      throw new Error(`Failed to list voices: ${response.statusText}`);
    }
    const payload = await response.json();
    const rawList = Array.isArray(payload)
      ? payload
      : payload?.voices ?? payload?.data ?? [];
    if (!Array.isArray(rawList)) {
      return [];
    }
    return rawList.map((v: any) => ({
      id: v.voice_id ?? v.id ?? v.external_id ?? String(v.name ?? 'unknown'),
      name: v.name ?? v.label ?? String(v.voice_id ?? v.id ?? 'Unnamed'),
    }));
  }
}
